import { PUMP_LIST, PUMP_METRIC_LIST, type PumpId } from "./pumps.js";

export type AlertParameterKey = string;

export interface AlertParameterDef {
  key: string;
  referenceId: string;
  displayName: string;
  unit: string;
  pumpId?: PumpId;
}

const CORE_PARAMETERS: Record<string, AlertParameterDef> = {
  jackingForce:       { key: "jackingForce",       referenceId: "V15_VTP_D_Kraft_Z",       displayName: "Jacking Force",              unit: "kN"  },
  torque:             { key: "torque",             referenceId: "V13_SR_ArbDr_Z",          displayName: "Cutterhead Torque",          unit: "bar" },
  facePressure:       { key: "facePressure",       referenceId: "V13_Erddruck_01_Z",       displayName: "Face Pressure",              unit: "bar" },
  totalScForce:       { key: "totalScForce",       referenceId: "V18_GesamtKraft_STZ_Z",   displayName: "Total SC Force",             unit: "kN"  },
  feedLinePressure:   { key: "feedLinePressure",   referenceId: "V21_SL_Dr_Z",             displayName: "Feed Line Pressure",         unit: "bar" },
  slurryLinePressure: { key: "slurryLinePressure", referenceId: "V21_FL_Dr_Z",             displayName: "Slurry Line Pressure",       unit: "bar" },
  oilTempContainer:   { key: "oilTempContainer",   referenceId: "V11_Oeltemp_C_Z",         displayName: "Oil Temp (Container)",       unit: "°C"  },
  oilTempMachine:     { key: "oilTempMachine",     referenceId: "V11_Oeltemp_M_Z",         displayName: "Oil Temp (Machine)",         unit: "°C"  },
  coolingWaterTemp:   { key: "coolingWaterTemp",   referenceId: "V11_Kuehlwassser_Temp_Z", displayName: "Cooling Water Temp",         unit: "°C"  },
  oilTempStatus:      { key: "oilTempStatus",      referenceId: "V11_Oeltemp_Status_Z",    displayName: "Oil Temp Status",            unit: ""    },
  bulkingFactor:      { key: "bulkingFactor",      referenceId: "__computed_bf__",          displayName: "Bulking Factor (smoothed)",  unit: ""    },
};

function buildPumpParameters(): Record<string, AlertParameterDef> {
  const out: Record<string, AlertParameterDef> = {};
  for (const pump of PUMP_LIST) {
    for (const metric of PUMP_METRIC_LIST) {
      const key = `pump_${pump.id}_${metric.id}`;
      out[key] = {
        key,
        referenceId: pump.refPrefix + metric.suffix,
        displayName: `${pump.displayName} ${metric.label}`,
        unit: metric.unit,
        pumpId: pump.id,
      };
    }
  }
  return out;
}

export const ALERT_PARAMETERS: Record<string, AlertParameterDef> = {
  ...CORE_PARAMETERS,
  ...buildPumpParameters(),
};

export const ALERT_PARAMETER_LIST: AlertParameterDef[] = Object.values(ALERT_PARAMETERS);

export const ALERT_SENSOR_REFERENCE_IDS: string[] = [];

export type AlertDirection = "above" | "below";

export interface AlertConfig {
  id: number;
  parameterKey: AlertParameterKey;
  displayName: string;
  referenceId: string;
  direction: AlertDirection;
  threshold: number;
  unit: string;
  enabled: boolean;
  cooldownMinutes: number;
  hysteresisPct: number;
  createdAt: string;
}

export interface AlertRecipient {
  id: number;
  email: string;
  name: string;
  enabled: boolean;
  createdAt: string;
}

export interface AlertEvent {
  id: number;
  alertConfigId: number;
  parameterKey: string;
  displayName: string;
  direction: AlertDirection;
  triggeredAt: string;
  value: number;
  threshold: number;
  unit: string;
  notified: boolean;
  resolvedAt: string | null;
}

export interface AlertCreateBody {
  parameterKey: AlertParameterKey;
  direction: AlertDirection;
  threshold: number;
  cooldownMinutes?: number;
  hysteresisPct?: number;
}

export interface AlertRecipientCreateBody {
  email: string;
  name?: string;
}

export interface AlertStatusItem {
  configId: number;
  parameterKey: string;
  displayName: string;
  direction: AlertDirection;
  threshold: number;
  unit: string;
  currentValue: number | null;
  breached: boolean;
  lastBreachAt: string | null;
  activeEventId: number | null;
}

export interface AlertsStatusResponse {
  alerts: AlertStatusItem[];
  emailConfigured: boolean;
}
